"use client";

import { imageApiUrl } from "@/lib/images/urls";

import Image from "next/image";
import Link from "next/link";
import { useMessages } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

import type { SiteCopy, SiteLanguage } from "@/lib/site-data";
import type { DashboardData } from "@/lib/site-queries";
import type { ClusterRecord } from "@/lib/data/shared/types";
import { BandBadge } from "@/components/site/band-badge";
import { cn } from "@/lib/utils";

// How many cards to pull per server request. Small enough that each
// round trip feels instant on flaky connections; large enough that we
// don't hammer the DB every 500 px of scroll.
const PAGE_SIZE = 24;

function pickTranslation(
  translations: Array<{ language: string; title: string; summary: string }>,
  language: string,
) {
  return translations.find((item) => item.language === language) ?? translations[0] ?? null;
}

function urlHostname(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    return host;
  } catch {
    return "source";
  }
}

/**
 * Stories (`/clusters`) — infinite scroll, Google-News-shape cards with
 * Intercept visual tuning. The server renders the first page in
 * `DashboardData.clusters` (pre-warmed). As the user scrolls past a
 * sentinel 700px before the bottom, we fetch the next page from
 * `/api/clusters?cursor=...`. Keyset cursor, so no offset penalty even
 * when the catalog grows.
 */
export function ClustersView({ data, language }: { data: DashboardData; language: SiteLanguage }) {
  const copy = useMessages() as SiteCopy;
  const [rows, setRows] = useState<ClusterRecord[]>(() => data.clusters);
  // The server-rendered prefix was ordered by updatedAt desc, so the
  // last row's updatedAt + id is where we resume the keyset scan.
  const [nextCursor, setNextCursor] = useState<string | null>(() => {
    const last = data.clusters[data.clusters.length - 1];
    return last ? `${last.updatedAt}:${last.id}` : null;
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(data.clusters.length === 0);
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const markFailed = (id: string) =>
    setFailed((current) => {
      if (current.has(id)) return current;
      const next = new Set(current);
      next.add(id);
      return next;
    });

  const fetchMore = useCallback(async () => {
    if (loadingMore || exhausted || !nextCursor) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({ cursor: nextCursor, limit: String(PAGE_SIZE) });
      const response = await fetch(`/api/clusters?${params.toString()}`, { cache: "no-store" });
      if (!response.ok) throw new Error(`clusters feed ${response.status}`);
      const payload = (await response.json()) as { items: ClusterRecord[]; nextCursor: string | null };
      setRows((current) => {
        // Dedup defensively — if the keyset cursor and the prefetched
        // last row agree we might otherwise double-count the boundary.
        const seen = new Set(current.map((row) => row.id));
        const fresh = payload.items.filter((row) => !seen.has(row.id));
        return [...current, ...fresh];
      });
      setNextCursor(payload.nextCursor);
      if (!payload.nextCursor) setExhausted(true);
    } catch {
      // Silent-retry on the next intersect; we don't want a single
      // flaky request to break the scroll.
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, exhausted, nextCursor]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || exhausted || !nextCursor) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void fetchMore();
        }
      },
      { rootMargin: "700px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchMore, exhausted, nextCursor]);

  const [lead, ...rest] = rows;

  return (
    <section className="space-y-8">
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-[0.18em] text-rose-600 dark:text-rose-300">duudey / {copy.nav.clusters}</p>
        <h1 className="text-4xl font-black leading-tight text-foreground sm:text-5xl">{copy.sections.clusteredStories}</h1>
      </header>

      {lead ? <LeadCard cluster={lead} copy={copy} language={language} failed={failed} onImageFail={markFailed} /> : null}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {rest.map((cluster) => (
          <StoryCard
            key={cluster.id}
            cluster={cluster}
            copy={copy}
            language={language}
            imageFailed={failed.has(cluster.id)}
            onImageFail={() => markFailed(cluster.id)}
          />
        ))}
      </div>

      {/* Sentinel + loading hint. Hidden when we know we've hit the
          end so the UI doesn't spin forever on short catalogs. */}
      {!exhausted ? (
        <div ref={sentinelRef} className="flex justify-center py-8 text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground">
          {loadingMore ? copy.ui.loadingMore : ""}
        </div>
      ) : null}
    </section>
  );
}

type CardCluster = ClusterRecord;

function LeadCard({
  cluster,
  copy,
  language,
  failed,
  onImageFail,
}: {
  cluster: CardCluster;
  copy: SiteCopy;
  language: SiteLanguage;
  failed: Set<string>;
  onImageFail: (id: string) => void;
}) {
  const translation = pickTranslation(cluster.translations, language);
  const showImage = cluster.hasImage && !failed.has(cluster.id);

  return (
    <article className="group relative overflow-hidden rounded-3xl border border-border bg-card/60">
      <div className="relative aspect-[16/8] w-full overflow-hidden bg-muted">
        {showImage ? (
          <Image
            src={imageApiUrl("cluster", cluster.id)}
            alt={translation?.title ?? "Lead story"}
            fill
            priority
            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 90vw, 1280px"
            className="object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            onError={() => onImageFail(cluster.id)}

          />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/40 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-5 sm:p-8">
          <span className="w-fit rounded-full bg-rose-500/90 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
            {copy.ui.topWire}
          </span>
          <Link
            href={`/${language}/clusters/${cluster.id}`}
            className="block text-2xl font-black leading-tight text-white hover:text-rose-200 sm:text-4xl"
          >
            {translation?.title ?? copy.ui.untitledStory}
          </Link>
          <p className="max-w-3xl text-sm leading-relaxed text-white/80 sm:text-base">
            {translation?.summary ?? copy.ui.noSummaryYet}
          </p>
          <OutletRow cluster={cluster} copy={copy} tone="dark" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-5 py-4 sm:px-8">
        {cluster.bands.slice(0, 6).map((band) => (
          <BandBadge key={band.name} band={band} language={language} />
        ))}
      </div>
    </article>
  );
}

function StoryCard({
  cluster,
  copy,
  language,
  imageFailed,
  onImageFail,
}: {
  cluster: CardCluster;
  copy: SiteCopy;
  language: SiteLanguage;
  imageFailed: boolean;
  onImageFail: () => void;
}) {
  const translation = pickTranslation(cluster.translations, language);
  const showImage = cluster.hasImage && !imageFailed;

  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card/50 transition hover:border-rose-400/40">
      <Link href={`/${language}/clusters/${cluster.id}`} className="relative block aspect-[16/9] w-full overflow-hidden bg-muted">
        {showImage ? (
          <Image
            src={imageApiUrl("cluster", cluster.id)}
            alt={translation?.title ?? "Story"}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 420px"
            className="object-cover transition-transform duration-500 hover:scale-[1.03]"
            onError={onImageFail}

          />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
          <span className="rounded-full border border-rose-300/40 bg-rose-400/10 px-2 py-0.5 text-rose-700 dark:text-rose-200">
            {cluster.sourceNames.length} {copy.ui.sources.toLowerCase()}
          </span>
          <span>·</span>
          <span>
            {cluster.articleCount} {copy.ui.articles.toLowerCase()}
          </span>
        </div>

        <Link href={`/${language}/clusters/${cluster.id}`} className="text-lg font-bold leading-snug text-foreground hover:text-rose-600 dark:hover:text-rose-300">
          {translation?.title ?? copy.ui.untitledStory}
        </Link>

        <p className="text-sm leading-relaxed text-muted-foreground line-clamp-3">
          {translation?.summary ?? copy.ui.noSummaryYet}
        </p>

        <OutletRow cluster={cluster} copy={copy} tone="light" />

        <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-2">
          {cluster.bands.slice(0, 4).map((band) => (
            <BandBadge key={band.name} band={band} language={language} />
          ))}
        </div>
      </div>
    </article>
  );
}

function OutletRow({
  cluster,
  copy,
  tone,
}: {
  cluster: CardCluster;
  copy: SiteCopy;
  tone: "dark" | "light";
}) {
  const pairs = cluster.articleUrls.slice(0, 3).map((url, index) => ({
    url,
    outlet: cluster.sourceNames[index] ?? urlHostname(url),
  }));
  const extra = cluster.sourceNames.length - pairs.length;

  const base = "inline-flex items-center rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-[0.12em] transition";
  const pillClass =
    tone === "dark"
      ? "border border-white/20 bg-white/10 text-white hover:border-rose-300/60 hover:bg-rose-400/30"
      : "border border-border bg-muted text-foreground hover:border-rose-300/60 hover:bg-rose-400/10";
  const moreClass =
    tone === "dark"
      ? "border border-white/10 bg-white/5 text-white/70"
      : "border border-border bg-transparent text-muted-foreground";

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {pairs.map(({ url, outlet }) => (
        <a
          key={url}
          href={url}
          target="_blank"
          rel="noreferrer"
          className={cn(base, pillClass)}
        >
          {outlet}
        </a>
      ))}
      {extra > 0 ? <span className={cn(base, moreClass)}>+{extra} {copy.ui.sources.toLowerCase()}</span> : null}
    </div>
  );
}
